import { ActivityOrigin, buildActivityOrigin } from '@api/ActivityLogs/ActivityOrigin';
import { InventoryOrder, InventoryOrderStatus } from '@api/InventoryOrders/InventoryOrder';
import { cashText } from '@api/ActivityLogs/CashActivity';

/**
 * Qué se audita de los PEDIDOS AL CEDIS (M6 del `PLAN-BITACORA-ACTIVITY-LOGS.md`).
 *
 * El circuito CEDIS ↔ sucursal mueve mercancía real y su cadena de estados
 * —`requested → pickupReady → inRoute → received`— no dejaba **ningún** rastro
 * de quién la movió. Importa porque hay un momento irreversible en ella:
 * **marcar "Recibido" suma al inventario de la sucursal y descuenta del CEDIS**,
 * y se puede hacer en MASA desde la pantalla del CEDIS.
 *
 * ⚠️ **Lo que se registra es el ACTO de la persona, no el movimiento de stock**
 * (D6): el stock ya tiene su propio rastro, y mejor — el ledger
 * `inventory-movements` guarda cada entrada y salida en unidades base, con su
 * `referenceId` idempotente. Duplicarlo aquí sería contar el mismo hecho dos
 * veces y en dos unidades distintas.
 */

const ESTADOS: Record<string, string> = {
    [InventoryOrderStatus.requested]: 'solicitado',
    [InventoryOrderStatus.pickupReady]: 'listo para recoger',
    [InventoryOrderStatus.inRoute]: 'en ruta',
    [InventoryOrderStatus.received]: 'recibido',
    [InventoryOrderStatus.canceled]: 'cancelado'
};

/** El nombre del estado en español, o el crudo si aparece uno nuevo. */
export const getInventoryOrderStatusLabel = (status: InventoryOrderStatus): string => ESTADOS[status] || status;

/**
 * El cambio de estado. **"Recibido" se dice distinto** porque no es un estado
 * más: es el que mueve el inventario de dos lugares a la vez y no se deshace.
 */
export const describeInventoryOrderStatus = (status: InventoryOrderStatus, enMasa = false): string => {
    const sufijo = enMasa ? ' (en masa desde el CEDIS)' : '';
    if (status === InventoryOrderStatus.received) {
        return `marcó el pedido como RECIBIDO${sufijo}: entró al inventario de la sucursal y salió del CEDIS`;
    }
    return `cambió el pedido a ${getInventoryOrderStatusLabel(status)}${sufijo}`;
};

/** El cobro del traspaso: `inventory-orders` lleva su propio modelo de pago. */
export const describeInventoryOrderPayment = (total: number): string =>
    `registró el pago del pedido por ${cashText(total)}`;

/** Alguien editó lo que se pidió después de crearlo. */
export const describeInventoryOrderItems = (antes: number, despues: number): string =>
    `cambió los insumos del pedido (de ${antes} a ${despues} renglones)`;

/**
 * El primer renglón de un pedido al CEDIS, DERIVADO (D8).
 *
 * ⚠️ **Es el caso de prueba del renglón que tolera la ausencia del autor**:
 * medido contra producción, `createdAt` está en **959 de 959** pedidos y
 * `createdBy` solo en el **27 %** — todos los recientes, ninguno de 2023. El
 * campo se agregó en el camino, que es la forma que tiene todo campo sembrado
 * hacia adelante. Con autor dice *"Joan Guemes creó el pedido"*; sin él,
 * *"Pedido creado"* — que es información honesta, no un hueco.
 */
export const buildInventoryOrderOrigin = (inventoryOrder?: InventoryOrder): ActivityOrigin[] => {
    const origin = buildActivityOrigin(inventoryOrder as never, {
        verb: 'creó el pedido de insumos',
        noun: 'Pedido de insumos creado'
    });
    return origin ? [origin] : [];
};
